import React, { useEffect, useState, useCallback } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { CalendarRange, Printer, FileSpreadsheet, Download, Trash2, RefreshCw, ChevronDown, Save } from "lucide-react";

const CADENCES = [["suppress", "Suppress"], ["monthly", "Monthly"], ["bi", "Every 2 months"], ["quarterly", "Quarterly"]];
const RANGE_PRESETS = [["15", "15 days"], ["30", "30 days"], ["60", "60 days"], ["90", "90 days"], ["365", "1 year"], ["custom", "Custom"]];
const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const f = new Date(); f.setDate(f.getDate() - n); return iso(f); };
const money = (n) => `$${(parseFloat(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function BoxAlongStatements({ category, items = [] }) {
  const idsCsv = (items || []).map((i) => i.id).filter(Boolean).join(",");
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState("positions");
  const [preset, setPreset] = useState("30");
  const [from, setFrom] = useState(() => daysAgo(30));
  const [to, setTo] = useState(() => iso(new Date()));
  const [data, setData] = useState({ positions: [], transactions: [], label: category, item_count: 0 });
  const [cfg, setCfg] = useState({ frequency: "monthly", statement_date: 1, email_enabled: false, recipient: "", alert_enabled: false });
  const [archive, setArchive] = useState([]);
  const [msg, setMsg] = useState("");

  const params = () => ({ category, focus, ...(idsCsv ? { ids: idsCsv } : {}), ...(from ? { frm: from } : {}), ...(to ? { to } : {}) });

  const loadRows = useCallback(() => {
    client.get("/boxalong/statement/rows", { params: params() })
      .then((r) => setData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, focus, from, to, idsCsv]);

  const loadSchedule = useCallback(() => {
    client.get("/boxalong/statement/config", { params: { scope: category } }).then((r) => setCfg(r.data)).catch(() => {});
    client.get("/boxalong/statement/archive", { params: { scope: category } }).then((r) => setArchive(r.data.statements || [])).catch(() => {});
  }, [category]);

  useEffect(() => { if (open) { loadRows(); loadSchedule(); } }, [open, loadRows, loadSchedule]);

  const applyPreset = (v) => { setPreset(v); if (v !== "custom") { setFrom(daysAgo(Number(v))); setTo(iso(new Date())); } };

  const dl = async (fmt) => {
    try {
      const res = await client.get(`/boxalong/statement/export.${fmt}`, { params: params(), responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `boxalong_${category}.${fmt}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { setMsg("Export failed."); }
  };
  const printPdf = async () => {
    try {
      const res = await client.get("/boxalong/statement/export.pdf", { params: params(), responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const w = window.open(url, "_blank"); if (w) w.onload = () => w.print();
    } catch { setMsg("Print failed."); }
  };
  const saveCfg = async (patch = {}) => {
    const next = { ...cfg, ...patch, scope: category };
    setCfg(next);
    try { await client.put("/boxalong/statement/config", next); setMsg("Schedule saved."); setTimeout(() => setMsg(""), 2000); }
    catch { setMsg("Could not save schedule."); }
  };
  const generateNow = async () => {
    try {
      await client.post("/boxalong/statement/generate", { scope: category, ids: (items || []).map((i) => i.id).filter(Boolean) });
      setMsg("Statement generated & archived."); setTimeout(() => setMsg(""), 2500);
      client.get("/boxalong/statement/archive", { params: { scope: category } }).then((r) => setArchive(r.data.statements || [])).catch(() => {});
    } catch { setMsg("Could not generate statement."); }
  };
  const downloadStored = async (sid) => {
    try {
      const res = await client.get(`/boxalong/statement/archive/${sid}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `boxalong_statement.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { setMsg("Download failed."); }
  };
  const deleteStored = async (sid) => {
    if (!window.confirm("Remove this stored statement?")) return;
    try { await client.delete(`/boxalong/statement/archive/${sid}`); setArchive((a) => a.filter((s) => s.id !== sid)); }
    catch { setMsg("Could not delete."); }
  };

  const field = "rounded-md border border-black/15 dark:border-white/15 bg-white/70 dark:bg-black/30 px-2 py-1 text-sm";
  const tab = (v, l) => (
    <button key={v} type="button" onClick={() => setFocus(v)} data-testid={`box-stmt-focus-${v}`}
      className={`rounded-full px-3 py-1 text-xs font-medium transition ${focus === v ? "bg-[#C99415] text-white" : "border border-black/15 hover:bg-black/5 dark:border-white/20 dark:hover:bg-white/10"}`}>{l}</button>
  );

  return (
    <div className="rounded-lg border border-black/10 p-3 dark:border-white/10" data-testid={`box-statements-${category}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 text-sm font-semibold" data-testid={`box-stmt-toggle-${category}`} aria-expanded={open}>
        <CalendarRange size={16} className="text-[#C99415]" /> {TL("Statements —")} {TL(category)}
        <ChevronDown size={16} className={`ms-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {!open && <div className="mt-1 text-xs opacity-55">{TL("Click to view positions, transactions & schedule")}</div>}

      {open && (<div className="mt-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-medium uppercase tracking-wide opacity-55">{TL("Focus")}</span>
          {tab("positions", "Positions")}{tab("transactions", "Transactions")}
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-black/10 px-3 py-2 dark:border-white/10">
          <span className="text-[10px] font-medium uppercase tracking-wide opacity-55">{TL("Ad hoc range")}</span>
          {RANGE_PRESETS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => applyPreset(v)} data-testid={`box-stmt-range-${v}`}
              className={`rounded-full px-2.5 py-1 text-xs ${preset === v ? "bg-[#C99415] text-white" : "border border-black/15 hover:bg-black/5 dark:border-white/20"}`}>{l}</button>
          ))}
          {preset === "custom" && (<>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={field} data-testid="box-stmt-from" />
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={field} data-testid="box-stmt-to" />
          </>)}
          <div className="ms-auto flex items-center gap-1">
            <button type="button" onClick={printPdf} className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-xs dark:border-white/20" data-testid="box-stmt-print"><Printer size={13} /> {TL("Print")}</button>
            <button type="button" onClick={() => dl("pdf")} className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-xs dark:border-white/20" data-testid="box-stmt-pdf"><Download size={13} /> {TL("PDF")}</button>
            <button type="button" onClick={() => dl("csv")} className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-xs dark:border-white/20" data-testid="box-stmt-csv"><FileSpreadsheet size={13} /> {TL("CSV")}</button>
            <button type="button" onClick={loadRows} className="rounded-md border border-black/15 px-2 py-1 text-xs dark:border-white/20" data-testid="box-stmt-refresh"><RefreshCw size={13} /></button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10" data-testid={`box-stmt-table-${focus}`}>
          {focus === "positions" ? (
            <table className="w-full min-w-[720px] text-xs">
              <thead><tr className="bg-black/[0.03] dark:bg-white/[0.04] text-start">
                {["Item", "Sub-section", "Class", "Status", "Cost", "Gross", "Deprec./period", "Book value"].map((h) => <th key={h} className="p-2 text-start font-medium opacity-70">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.positions.map((r, i) => (
                  <tr key={i} className="border-t border-black/5 dark:border-white/5" data-testid={`box-pos-row-${i}`}>
                    <td className="p-2">{r.name}</td><td className="p-2">{r.subcategory || "—"}</td>
                    <td className="p-2">{r.asset_class || "—"}</td><td className="p-2">{r.status}</td>
                    <td className="p-2">{money(r.cost)}</td><td className="p-2">{money(r.gross_value)}</td>
                    <td className="p-2">{r.dep}</td><td className="p-2 font-medium">{money(r.book_value)}</td>
                  </tr>
                ))}
                {data.positions.length === 0 && <tr><td colSpan={8} className="p-4 text-center opacity-50">{TL("No positions in this category yet.")}</td></tr>}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[640px] text-xs">
              <thead><tr className="bg-black/[0.03] dark:bg-white/[0.04]">
                {["Date", "Type", "Item", "Currency", "Amount", "Note"].map((h) => <th key={h} className="p-2 text-start font-medium opacity-70">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.transactions.map((r, i) => (
                  <tr key={i} className="border-t border-black/5 dark:border-white/5" data-testid={`box-txn-row-${i}`}>
                    <td className="p-2">{r.date}</td><td className="p-2 capitalize">{r.kind}</td>
                    <td className="p-2">{r.name}</td><td className="p-2">{r.currency || "—"}</td>
                    <td className="p-2">{money(r.amount)}</td><td className="p-2">{r.note || "—"}</td>
                  </tr>
                ))}
                {data.transactions.length === 0 && <tr><td colSpan={6} className="p-4 text-center opacity-50">{TL("No transactions in this period.")}</td></tr>}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-black/10 px-3 py-2 dark:border-white/10">
          <span className="text-[10px] font-medium uppercase tracking-wide opacity-55">{TL("Scheduled statement")}</span>
          <select value={cfg.frequency} onChange={(e) => saveCfg({ frequency: e.target.value })} className={field} data-testid="box-stmt-cadence">
            {CADENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span className="text-xs opacity-60">{TL("Statement date")}</span>
          <input type="number" min={1} max={28} value={cfg.statement_date} onChange={(e) => setCfg({ ...cfg, statement_date: e.target.value })} onBlur={() => saveCfg()} className={`${field} w-16`} data-testid="box-stmt-date" />
          <label className="flex items-center gap-1 text-xs" data-testid="box-stmt-alert-label">
            <input type="checkbox" checked={cfg.alert_enabled} onChange={(e) => saveCfg({ alert_enabled: e.target.checked })} data-testid="box-stmt-alert" /> Alert
          </label>
          <label className="flex items-center gap-1 text-xs">
            <input type="checkbox" checked={cfg.email_enabled} onChange={(e) => saveCfg({ email_enabled: e.target.checked })} data-testid="box-stmt-email" /> Email
          </label>
          {cfg.email_enabled && (
            <input placeholder={TL("recipient@email")} value={cfg.recipient} onChange={(e) => setCfg({ ...cfg, recipient: e.target.value })} onBlur={() => saveCfg()} className={`${field} w-48`} data-testid="box-stmt-recipient" />
          )}
          <div className="ms-auto flex items-center gap-1">
            <button type="button" onClick={() => saveCfg()} className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-xs dark:border-white/20" data-testid="box-stmt-save"><Save size={13} /> {TL("Save schedule")}</button>
            <button type="button" onClick={generateNow} className="rounded-md bg-[#C99415] px-2 py-1 text-xs font-medium text-white" data-testid="box-stmt-generate">{TL("Generate statement now")}</button>
          </div>
        </div>

        {archive.length > 0 && (
          <div className="rounded-lg border border-black/10 dark:border-white/10" data-testid="box-stmt-archive">
            <div className="border-b border-black/10 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide opacity-55 dark:border-white/10">{TL("Stored statements (kept 15 months)")}</div>
            {archive.map((s) => (
              <div key={s.id} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`box-archive-${s.id}`}>
                <span className="font-medium">{s.frm} → {s.to}</span>
                {s.frequency && <span className="rounded-full bg-black/5 px-2 py-0.5 dark:bg-white/10">{s.frequency}</span>}
                <span className="opacity-60">{s.position_count} pos · {s.entry_count} txn</span>
                <span className="ms-auto flex items-center gap-1">
                  <button onClick={() => downloadStored(s.id)} className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 dark:border-white/20" data-testid={`box-archive-dl-${s.id}`}><Download size={12} /> {TL("PDF")}</button>
                  <button onClick={() => deleteStored(s.id)} className="opacity-60 hover:text-[#DA1E28]" data-testid={`box-archive-del-${s.id}`}><Trash2 size={13} /></button>
                </span>
              </div>
            ))}
          </div>
        )}

        {msg && <div className="text-xs font-medium text-[#C99415]" data-testid="box-stmt-msg">{msg}</div>}
      </div>)}
    </div>
  );
}
